import React, { useCallback, useState } from 'react';

import { Button, Spinner } from '@spark';

import { useAuth } from '@/features/auth/AuthContext.jsx';

import './authBoundary.css';

export const AUTH_ERROR_TITLE = 'Could not load your account';
export const AUTH_RETRY_LABEL = 'Retry';
export const AUTH_SIGN_OUT_LABEL = 'Sign out';

/** Neutral full-screen loader: plain canvas and a spinner, no navigation chrome. */
export function AuthBoundaryLoading() {
    return (
        <div className="auth-boundary auth-boundary-loading" data-testid="auth-boundary-loading">
            <Spinner size="large" aria-label="Loading your account" />
        </div>
    );
}

/**
 * Full-screen error with a way out.  `onRetry` re-runs the whole bootstrap;
 * `onSignOut` drops the session and lands on `/login`.
 */
export function AuthBoundaryError({ message, onRetry, onSignOut, busy = false }) {
    return (
        <div className="auth-boundary auth-boundary-error" role="alert">
            <div className="auth-boundary-card fs-unmask">
                <h1 className="auth-boundary-title">{AUTH_ERROR_TITLE}</h1>
                {message ? <p className="auth-boundary-message">{message}</p> : null}
                <div className="auth-boundary-actions">
                    <Button size="large" variant="primary" onClick={onRetry} disabled={busy}>
                        {AUTH_RETRY_LABEL}
                    </Button>
                    <Button size="large" variant="tertiary" onClick={onSignOut} disabled={busy}>
                        {AUTH_SIGN_OUT_LABEL}
                    </Button>
                </div>
            </div>
        </div>
    );
}

/**
 * Sits between `AuthProvider` and the route table so nothing route-specific
 * mounts before the session is known.
 *
 * - Until the first `POST /users/bootstrap` settles (`initialized === false`)
 *   only a neutral spinner renders: no navbar, no `GlobalProvider` fetches, so
 *   a stranger who is about to be sent to the waiting list never sees the
 *   shell flash, and an approved creator never sees a half-loaded workspace.
 * - While the provider still has a redirect to apply (`redirectPending`) the
 *   spinner stays up so the page being left is not mounted either.
 * - When the bootstrap fails (`authError`) a full-screen error replaces the
 *   old endless spinner, with "Retry" (re-run the bootstrap) and "Sign out".
 *
 * Mid-session `refresh()` calls do not re-trigger this screen: they flip
 * `loading`, which only the in-shell `AuthGate` spinner reacts to.
 */
export default function AuthBoundary({ children }) {
    const { initialized, redirectPending, authError, retryAuth, logout } = useAuth();
    const [busy, setBusy] = useState(false);

    const signOut = useCallback(async () => {
        if (busy) return;
        setBusy(true);
        try {
            // `logout` clears the local session and routes to /login itself.
            await logout();
        } catch (err) {
            // Only the backend call failed; the local session is already gone.
            console.warn('Logout failed', err);
        } finally {
            setBusy(false);
        }
    }, [busy, logout]);

    if (authError) {
        return <AuthBoundaryError message={authError} onRetry={retryAuth} onSignOut={signOut} busy={busy} />;
    }
    if (!initialized || redirectPending) {
        return <AuthBoundaryLoading />;
    }
    return children;
}
